/** Supported countries — company country is source of truth for phone/timezone defaults. */
import { US_STATES } from "../components/USStateConstant";

export const DEFAULT_COUNTRY = "US";

export const SUPPORTED_COUNTRY_CODES = ["US", "CA", "GB", "AU", "NZ", "IN"];

const CA_PROVINCES = [
  { value: "AB", label: "Alberta" },
  { value: "BC", label: "British Columbia" },
  { value: "MB", label: "Manitoba" },
  { value: "NB", label: "New Brunswick" },
  { value: "NL", label: "Newfoundland and Labrador" },
  { value: "NS", label: "Nova Scotia" },
  { value: "NT", label: "Northwest Territories" },
  { value: "NU", label: "Nunavut" },
  { value: "ON", label: "Ontario" },
  { value: "PE", label: "Prince Edward Island" },
  { value: "QC", label: "Quebec" },
  { value: "SK", label: "Saskatchewan" },
  { value: "YT", label: "Yukon" },
];

const GB_REGIONS = [
  { value: "England", label: "England" },
  { value: "Scotland", label: "Scotland" },
  { value: "Wales", label: "Wales" },
  { value: "Northern Ireland", label: "Northern Ireland" },
];

const AU_STATES = [
  { value: "ACT", label: "Australian Capital Territory" },
  { value: "NSW", label: "New South Wales" },
  { value: "NT", label: "Northern Territory" },
  { value: "QLD", label: "Queensland" },
  { value: "SA", label: "South Australia" },
  { value: "TAS", label: "Tasmania" },
  { value: "VIC", label: "Victoria" },
  { value: "WA", label: "Western Australia" },
];

const NZ_REGIONS = [
  { value: "Auckland", label: "Auckland" },
  { value: "Bay of Plenty", label: "Bay of Plenty" },
  { value: "Canterbury", label: "Canterbury" },
  { value: "Gisborne", label: "Gisborne" },
  { value: "Hawke's Bay", label: "Hawke's Bay" },
  { value: "Manawatu-Whanganui", label: "Manawatu-Whanganui" },
  { value: "Marlborough", label: "Marlborough" },
  { value: "Nelson", label: "Nelson" },
  { value: "Northland", label: "Northland" },
  { value: "Otago", label: "Otago" },
  { value: "Southland", label: "Southland" },
  { value: "Taranaki", label: "Taranaki" },
  { value: "Tasman", label: "Tasman" },
  { value: "Waikato", label: "Waikato" },
  { value: "Wellington", label: "Wellington" },
  { value: "West Coast", label: "West Coast" },
];

const IN_STATES = [
  { value: "AN", label: "Andaman and Nicobar Islands" },
  { value: "AP", label: "Andhra Pradesh" },
  { value: "AR", label: "Arunachal Pradesh" },
  { value: "AS", label: "Assam" },
  { value: "BR", label: "Bihar" },
  { value: "CH", label: "Chandigarh" },
  { value: "CT", label: "Chhattisgarh" },
  { value: "DN", label: "Dadra and Nagar Haveli and Daman and Diu" },
  { value: "DL", label: "Delhi" },
  { value: "GA", label: "Goa" },
  { value: "GJ", label: "Gujarat" },
  { value: "HR", label: "Haryana" },
  { value: "HP", label: "Himachal Pradesh" },
  { value: "JK", label: "Jammu and Kashmir" },
  { value: "JH", label: "Jharkhand" },
  { value: "KA", label: "Karnataka" },
  { value: "KL", label: "Kerala" },
  { value: "LA", label: "Ladakh" },
  { value: "LD", label: "Lakshadweep" },
  { value: "MP", label: "Madhya Pradesh" },
  { value: "MH", label: "Maharashtra" },
  { value: "MN", label: "Manipur" },
  { value: "ML", label: "Meghalaya" },
  { value: "MZ", label: "Mizoram" },
  { value: "NL", label: "Nagaland" },
  { value: "OR", label: "Odisha" },
  { value: "PY", label: "Puducherry" },
  { value: "PB", label: "Punjab" },
  { value: "RJ", label: "Rajasthan" },
  { value: "SK", label: "Sikkim" },
  { value: "TN", label: "Tamil Nadu" },
  { value: "TG", label: "Telangana" },
  { value: "TR", label: "Tripura" },
  { value: "UP", label: "Uttar Pradesh" },
  { value: "UT", label: "Uttarakhand" },
  { value: "WB", label: "West Bengal" },
];

/** @typedef {{ code: string, name: string, phoneCountryCode: string, defaultTimezone: string, timezones: { label: string, value: string }[], regions: { label: string, value: string }[], regionLabel: string, libPhoneCountry: string, nationalMaxLength: number }} CountryConfig */

/** @type {Record<string, CountryConfig>} */
export const SUPPORTED_COUNTRIES = {
  US: {
    code: "US",
    name: "United States",
    phoneCountryCode: "+1",
    defaultTimezone: "America/New_York",
    libPhoneCountry: "US",
    nationalMaxLength: 10,
    regionLabel: "State",
    regions: US_STATES,
    timezones: [
      { label: "EDT - Eastern", value: "America/New_York" },
      { label: "CDT - Central", value: "America/Chicago" },
      { label: "MDT - Mountain", value: "America/Denver" },
      { label: "PDT - Pacific", value: "America/Los_Angeles" },
    ],
  },
  CA: {
    code: "CA",
    name: "Canada",
    phoneCountryCode: "+1",
    defaultTimezone: "America/Toronto",
    libPhoneCountry: "CA",
    nationalMaxLength: 10,
    regionLabel: "Province",
    regions: CA_PROVINCES,
    timezones: [
      { label: "Toronto (ET)", value: "America/Toronto" },
      { label: "Vancouver (PT)", value: "America/Vancouver" },
      { label: "Edmonton (MT)", value: "America/Edmonton" },
      { label: "Winnipeg (CT)", value: "America/Winnipeg" },
      { label: "Halifax (AT)", value: "America/Halifax" },
      { label: "St. John's (NT)", value: "America/St_Johns" },
      { label: "Regina (CST)", value: "America/Regina" },
    ],
  },
  GB: {
    code: "GB",
    name: "United Kingdom",
    phoneCountryCode: "+44",
    defaultTimezone: "Europe/London",
    libPhoneCountry: "GB",
    nationalMaxLength: 10,
    regionLabel: "Country",
    regions: GB_REGIONS,
    timezones: [{ label: "London (GMT/BST)", value: "Europe/London" }],
  },
  AU: {
    code: "AU",
    name: "Australia",
    phoneCountryCode: "+61",
    defaultTimezone: "Australia/Sydney",
    libPhoneCountry: "AU",
    nationalMaxLength: 9,
    regionLabel: "State",
    regions: AU_STATES,
    timezones: [
      { label: "Sydney (AEST/AEDT)", value: "Australia/Sydney" },
      { label: "Melbourne (AEST/AEDT)", value: "Australia/Melbourne" },
      { label: "Brisbane (AEST)", value: "Australia/Brisbane" },
      { label: "Perth (AWST)", value: "Australia/Perth" },
      { label: "Adelaide (ACST/ACDT)", value: "Australia/Adelaide" },
      { label: "Hobart (AEST/AEDT)", value: "Australia/Hobart" },
      { label: "Darwin (ACST)", value: "Australia/Darwin" },
    ],
  },
  NZ: {
    code: "NZ",
    name: "New Zealand",
    phoneCountryCode: "+64",
    defaultTimezone: "Pacific/Auckland",
    libPhoneCountry: "NZ",
    nationalMaxLength: 10,
    regionLabel: "Region",
    regions: NZ_REGIONS,
    timezones: [
      { label: "Auckland (NZST/NZDT)", value: "Pacific/Auckland" },
      { label: "Chatham (CHAST/CHADT)", value: "Pacific/Chatham" },
    ],
  },
  IN: {
    code: "IN",
    name: "India",
    phoneCountryCode: "+91",
    defaultTimezone: "Asia/Kolkata",
    libPhoneCountry: "IN",
    nationalMaxLength: 10,
    regionLabel: "State",
    regions: IN_STATES,
    timezones: [{ label: "India (IST)", value: "Asia/Kolkata" }],
  },
};

export const COUNTRY_SELECT_OPTIONS = SUPPORTED_COUNTRY_CODES.map((code) => ({
  label: SUPPORTED_COUNTRIES[code].name,
  value: code,
}));

export function isSupportedCountry(country) {
  return SUPPORTED_COUNTRY_CODES.includes(String(country || "").toUpperCase());
}

export function getCountryConfig(country) {
  const code = String(country || DEFAULT_COUNTRY).toUpperCase();
  return SUPPORTED_COUNTRIES[isSupportedCountry(code) ? code : DEFAULT_COUNTRY];
}

export function getPhoneCountryCode(country) {
  return getCountryConfig(country).phoneCountryCode;
}

export function getDefaultTimezone(country) {
  return getCountryConfig(country).defaultTimezone;
}

export function getTimezoneOptions(country) {
  return getCountryConfig(country).timezones;
}

export function getRegionOptions(country) {
  return getCountryConfig(country).regions || US_STATES;
}

export function getRegionLabel(country) {
  return getCountryConfig(country).regionLabel || "State / Province";
}

/**
 * Resolve country from entity or company record. Legacy records without country default to US.
 */
export function resolveCountry(entity = {}, company = {}) {
  const raw =
    entity?.country ||
    company?.country ||
    entity?.companyCountry ||
    company?.companyCountry ||
    DEFAULT_COUNTRY;
  const code = String(raw).toUpperCase();
  return isSupportedCountry(code) ? code : DEFAULT_COUNTRY;
}

/**
 * Align stored phone metadata with company country (legacy US/+1 records on IN companies).
 */
export function reconcilePhoneCountryWithCompany(fields = {}, companyCountry) {
  if (!companyCountry) {
    return applyCompanyPhoneDefaults(fields, companyCountry);
  }

  const companyResolved = resolveCountry({}, { country: companyCountry });
  const entityCountry = fields?.country
    ? String(fields.country).toUpperCase()
    : "";
  const entityCode = fields?.phoneCountryCode;
  const hasSupportedEntityCountry =
    entityCountry && isSupportedCountry(entityCountry);

  const isLegacyUsOnInternationalCompany =
    hasSupportedEntityCountry &&
    entityCountry === DEFAULT_COUNTRY &&
    companyResolved !== DEFAULT_COUNTRY &&
    (!entityCode || entityCode === getCountryConfig(DEFAULT_COUNTRY).phoneCountryCode);

  if (!hasSupportedEntityCountry || isLegacyUsOnInternationalCompany) {
    return applyCompanyPhoneDefaults(
      isLegacyUsOnInternationalCompany
        ? { ...fields, country: undefined, phoneCountryCode: undefined }
        : fields,
      companyCountry
    );
  }

  const config = getCountryConfig(entityCountry);
  if (entityCode && entityCode !== config.phoneCountryCode) {
    return applyCompanyPhoneDefaults(
      { ...fields, phoneCountryCode: config.phoneCountryCode },
      entityCountry
    );
  }

  return applyCompanyPhoneDefaults(fields, entityCountry);
}

/**
 * Apply company country defaults to phone/timezone fields on create/edit payloads.
 */
export function applyCompanyPhoneDefaults(fields = {}, companyCountry) {
  const country = resolveCountry(fields, { country: companyCountry });
  const config = getCountryConfig(country);
  const storedCode = fields.phoneCountryCode;
  const phoneCountryCode =
    storedCode && storedCode === config.phoneCountryCode
      ? storedCode
      : config.phoneCountryCode;
  return {
    country,
    phoneCountryCode,
    phoneNumber: fields.phoneNumber ?? fields.mobileNumber ?? "",
    timeZone:
      fields.timeZone ||
      fields.timezone ||
      config.defaultTimezone,
  };
}

/** IANA timezone for appointments / punch — prefers stored calendar TZ, then client/employee site TZ. */
export function resolveEffectiveTimezone(record = {}, companyCountry) {
  const fromRecord =
    record.calendarTimezone ||
    record.clientDetails?.timeZone ||
    record.apoointmentDetails?.calendarTimezone ||
    record.timeZone ||
    record.timezone ||
    null;
  if (fromRecord) return fromRecord;
  if (companyCountry) return getDefaultTimezone(companyCountry);
  return getDefaultTimezone(DEFAULT_COUNTRY);
}
